import { createRng } from '../../../core/rng';
import { Grid, hits } from '../song';
import type { Note } from '../song';
import { barsOf, blend, cellsOf, chooser, cycle, find, fitForm, midi, noteName, pc, pcs, phrase, titleOf, totalBars, trackId, transpose } from '../score';
import type { Ev, FormPlan, Params, Score, Template } from '../score';

type Chord = { root: string; fifth: string; button: readonly string[]; full: readonly string[] };

const CHORDS: Readonly<Record<string, Chord>> = {
  C: { root: 'C2', fifth: 'G1', button: ['C4', 'E4', 'G4'], full: ['G3', 'C4', 'E4', 'G4'] },
  C7: { root: 'C2', fifth: 'G1', button: ['C4', 'E4', 'Bb4'], full: ['G3', 'C4', 'E4', 'Bb4'] },
  F: { root: 'F2', fifth: 'C2', button: ['C4', 'F4', 'A4'], full: ['A3', 'C4', 'F4', 'A4'] },
  G7: { root: 'G2', fifth: 'D2', button: ['B3', 'D4', 'F4'], full: ['G3', 'B3', 'D4', 'F4'] },
  Bb: { root: 'Bb1', fifth: 'F2', button: ['D4', 'F4', 'Bb4'], full: ['F3', 'Bb3', 'D4', 'F4'] },
  Dm: { root: 'D2', fifth: 'A1', button: ['D4', 'F4', 'A4'], full: ['A3', 'D4', 'F4', 'A4'] },
  Am: { root: 'A1', fifth: 'E2', button: ['C4', 'E4', 'A4'], full: ['A3', 'C4', 'E4', 'A4'] },
};

const PROGS = [
  ['C', 'C7', 'F', 'C', 'G7', 'G7', 'C', 'C'],
  ['C', 'C', 'G7', 'G7', 'G7', 'G7', 'C', 'C'],
  ['C', 'Bb', 'C', 'Bb', 'F', 'G7', 'C', 'C'],
  ['Am', 'Am', 'Dm', 'Dm', 'G7', 'G7', 'C', 'C'],
  ['C', 'F', 'C', 'G7', 'C', 'F', 'G7', 'C'],
] as const;

const HOOK: Note[][] = [
  [[0, 'G4', 2], [2, 'C5', 2], [4, 'E5', 2], [6, 'G5', 3], [9, 'F5', 1, 0.7], [10, 'E5', 2], [12, 'D5', 2], [14, 'C5', 2]],
  [[0, 'Bb4', 4], [4, 'C5', 2], [6, 'E5', 2], [8, 'G5', 2], [10, 'Bb5', 4], [14, 'A5', 2]],
  [[0, 'A5', 3], [3, 'F5', 3], [6, 'A5', 2], [8, 'C6', 4], [12, 'A5', 2], [14, 'G5', 2]],
  [[0, 'E5', 3], [3, 'G5', 3], [6, 'E5', 2], [8, 'C5', 6], [14, 'D5', 2]],
  [[0, 'F5', 2], [2, 'F5', 2], [4, 'G5', 2], [6, 'F5', 2], [8, 'D5', 3], [11, 'B4', 3], [14, 'D5', 2]],
  [[0, 'F5', 3], [3, 'E5', 1, 0.7], [4, 'D5', 2], [6, 'B4', 2], [8, 'G4', 4], [12, 'A4', 2], [14, 'B4', 2]],
  [[0, 'C5', 2], [2, 'E5', 2], [4, 'G5', 2], [6, 'Bb5', 3], [9, 'A5', 1, 0.7], [10, 'G5', 2], [12, 'E5', 2], [14, 'D5', 2]],
  [[0, 'C5', 8], [8, 'G4', 2], [10, 'A4', 2], [12, 'Bb4', 2], [14, 'B4', 2]],
];

const KEYS = [0, 2, -3, -5, 5, -2, 3] as const;
const NORDESTINO = [0, 2, 4, 5, 7, 9, 10] as const;
const XOTES = ['X.....x.X.....x.', 'X.....x.X...x.x.', 'X....xx.X.....x.'] as const;
const XOTE_FILLS = ['X.....x.X...x.xx', 'X.....x.X.x.x.x.'] as const;
const BAIAO = 'X..x....X..x....';
const BAIAO_FILL = 'X..x....X.x.x.xx';
const VIRADA = 'X.....x.x.xxXxXX';
const TA = '....X.......X...';
const BACALHAUS = ['..x...x...x...x.', '..x..x.x..x..x.x'] as const;
const TRI_CLOSED = 'X...x...X...x..o';
const TRI_OPEN = '..x...x...x...x.';
const BAIAO_TRI = 'xo.oxo.oxo.oxo.o';
const BAIAO_OPEN = '..X...X...X...X.';
const ACORDES = ['....X.......X...', '....X.o.....X.o.'] as const;

const PLAN: FormPlan = {
  intro: 4,
  outro: 4,
  body: [
    { name: 'A', bars: 8, min: 4, rank: 2 },
    { name: 'drop', bars: 8, min: 4, rank: 1 },
    { name: 'break', bars: 4, min: 4, rank: 4, with: 'drop2' },
    { name: 'drop2', bars: 8, min: 4, rank: 3 },
  ],
};

function under(note: string, tones: readonly number[]): string {
  const m = midi(note);
  const scale = blend([...NORDESTINO], tones);
  for (const d of [3, 4]) if (scale.includes(pc(m - d))) return noteName(m - d);
  for (let d = 1; d <= 6; d++) if (tones.includes(pc(m - d))) return noteName(m - d);
  return noteName(m - 3);
}

function score(seed: string, p: Params): Score {
  const ch = chooser(XOTE, seed);
  const key = ch.pick(KEYS);
  const prog = ch.pick(PROGS).map((n) => CHORDS[n]!);
  const zabP = ch.pick(XOTES);
  const fillP = ch.pick(XOTE_FILLS);
  const bacP = ch.pick(BACALHAUS);
  const hook = ch.orig
    ? HOOK
    : phrase(
        ch.rng,
        HOOK.map(cellsOf),
        prog.map((c) => [{ at: 0, tones: pcs([c.root, ...c.button]) }]),
        NORDESTINO,
        [67, 84],
        76,
      );
  const e = p.energy;
  const tr = (n: string) => transpose(n, key);
  const low = (n: string) => {
    let m = midi(tr(n));
    while (m < 36) m += 12;
    while (m > 47) m -= 12;
    return noteName(m);
  };

  const form = fitForm(barsOf(p), PLAN);
  const g = new Grid(p.bpm, 56, form);
  const s = g.step;
  const intro = form[0]!.bars;
  const total = totalBars(form);
  const drop1 = find(form, 'drop')!;
  const drop2 = find(form, 'drop2');
  const rng = createRng(`xote-${seed}`);
  const human = (v: number) => v * (0.92 + rng.float() * 0.16);

  const events: Ev[] = [];
  const hit = (v: string, bar: number, step: number, vel: number) => events.push({ v, t: g.at(bar, step), bar, step, vel });
  const play = (v: string, bar: number, step: number, note: string, dur: number, vel: number) =>
    events.push({ v, t: g.at(bar, step), bar, step, vel, dur, note });
  const chord = (v: string, bar: number, step: number, notes: readonly string[], dur: number, vel: number, strum = 0) =>
    events.push({ v, t: g.at(bar, step), bar, step, vel, dur, notes, strum });

  const autos: Score['autos'] = [
    { v: 'foleLp', points: [[g.at(0), 1500], [g.at(intro), 3800]] },
    { v: 'foleLp', points: [[g.at(total - 1), 3800], [g.at(total), 1600]] },
  ];

  for (const b of g.bars()) {
    const i = cycle(b.bar - intro, 8);
    const end = b.bar === total - 1;
    const c = end ? CHORDS.C! : prog[i]!;
    const next = prog[cycle(i + 1, 8)]!;
    const tones = pcs([c.root, ...c.button]);
    const drop = b.name === 'drop' || b.name === 'drop2';
    const baiao = b.name === 'drop2' && e >= 0.85;
    const band = drop || b.name === 'A' || (b.name === 'outro' && b.index < b.bars / 2);
    const virada = b.last && (b.bar + 1 === drop1 || b.bar + 1 === drop2);
    const button = c.button.map(tr);

    if (end) {
      hit('zabumba', b.bar, 0, 1);
      chord('acorde', b.bar, 0, c.full.map(tr), s * 3, 0.95);
      chord('pad', b.bar, 0, c.full.map(tr), g.barLength * 0.9, 0.7, 0.01);
      play('baixo', b.bar, 0, low(c.root), g.barLength * 0.5, 0.9);
      hit('triO', b.bar, 0, 0.8);
      continue;
    }

    if (band) {
      if (virada) {
        for (const h of hits(VIRADA)) hit('zabumba', b.bar, h.step, human(h.vel * (0.6 + (0.4 * h.step) / 15)));
        for (let k = 8; k < 16; k++) chord('fole', b.bar, k, button, s * 0.6, 0.4 + 0.05 * (k - 8));
      } else {
        const pattern = baiao ? (b.index % 4 === 3 ? BAIAO_FILL : BAIAO) : b.index % 4 === 3 ? fillP : zabP;
        for (const h of hits(pattern)) hit('zabumba', b.bar, h.step, human(h.vel));
      }
      for (const h of hits(TA)) hit('ta', b.bar, h.step, human(0.86));
      for (const h of hits(baiao ? BACALHAUS[0] : bacP)) hit('bacalhau', b.bar, h.step, human(h.vel * 0.85));
      for (const h of hits(baiao ? BAIAO_TRI : TRI_CLOSED)) hit('triC', b.bar, h.step, human(h.vel));
      for (const h of hits(baiao ? BAIAO_OPEN : TRI_OPEN)) hit('triO', b.bar, h.step, human(h.vel * 0.85));
      const root = low(c.root);
      const fifth = low(c.fifth);
      if (baiao) {
        play('baixo', b.bar, 0, root, s * 2.6, human(0.9));
        play('baixo', b.bar, 3, root, s * 0.9, human(0.7));
        play('baixo', b.bar, 8, fifth, s * 2.6, human(0.85));
        play('baixo', b.bar, 11, fifth, s * 0.9, human(0.7));
      } else {
        play('baixo', b.bar, 0, root, s * 3, human(0.9));
        play('baixo', b.bar, 8, fifth, s * 3, human(0.8));
        if (drop && !b.last) play('baixo', b.bar, 14, noteName(midi(low(next.root)) - 1), s * 1.6, human(0.6));
      }
      if (!virada) for (const h of hits(drop ? ACORDES[1] : ACORDES[0])) chord('acorde', b.bar, h.step, button, s * 1.1, human(h.vel * 0.9));
    } else if (b.name === 'break') {
      if (virada) {
        for (const h of hits(VIRADA)) hit('zabumba', b.bar, h.step, human(h.vel * (0.55 + (0.45 * h.step) / 15)));
        for (let k = 8; k < 16; k++) chord('fole', b.bar, k, button, s * 0.6, 0.4 + 0.05 * (k - 8));
      } else {
        for (const h of hits('X.......X.......')) hit('zabumba', b.bar, h.step, human(0.86));
      }
      for (const h of hits(TRI_CLOSED)) hit('triC', b.bar, h.step, human(h.vel * 0.8));
      for (const h of hits(TRI_OPEN)) hit('triO', b.bar, h.step, human(h.vel * 0.6));
      play('baixo', b.bar, 0, low(c.root), s * 6, 0.7);
      chord('pad', b.bar, 0, c.full.map(tr), g.barLength * 0.96, 0.55, 0.015);
    } else if (b.name === 'intro') {
      chord('pad', b.bar, 0, c.full.map(tr), g.barLength * 0.96, 0.5, 0.015);
      if (b.index >= b.bars / 2) for (const h of hits(TRI_CLOSED)) hit('triC', b.bar, h.step, human(h.vel * 0.7));
      if (b.last) for (const h of hits('........x...x.x.')) hit('zabumba', b.bar, h.step, 0.7);
    } else if (b.name === 'outro') {
      chord('pad', b.bar, 0, c.full.map(tr), g.barLength * 0.96, 0.55, 0.015);
      play('baixo', b.bar, 0, low(c.root), s * 4, 0.7);
      for (const h of hits(TRI_OPEN)) hit('triO', b.bar, h.step, human(h.vel * 0.55));
    }

    const row = hook[i]!;
    const lead = (vel: number, harm: number, octave: boolean, grace: boolean) => {
      for (const [step, note, len, v] of row) {
        const n = tr(note);
        play('lead', b.bar, step, n, len * s * 0.9, human((v ?? 0.95) * vel));
        if (harm > 0) play('harm', b.bar, step, tr(under(note, tones)), len * s * 0.88, (v ?? 0.9) * harm);
        if (octave) play('harm', b.bar, step, transpose(n, -12), len * s * 0.86, (v ?? 0.9) * 0.45);
        if (grace && len >= 3 && step % 4 === 0) {
          events.push({ v: 'grace', t: g.at(b.bar, step) - s * 0.5, bar: b.bar, step, vel: 0.5, dur: s * 0.45, note: transpose(n, -1) });
        }
      }
    };
    if (b.name === 'A') lead(0.85, 0, false, e >= 0.7);
    if (b.name === 'drop') lead(1, 0.55, e >= 0.85, true);
    if (b.name === 'drop2') lead(1, 0.6, e >= 0.7, true);
    if (b.name === 'break' && b.index < b.bars / 2) lead(0.75, 0, false, false);
    if (b.name === 'outro' && b.index < b.bars / 2) lead(0.7, 0, false, false);
  }

  for (const bar of [drop1, drop2]) {
    if (bar === undefined) continue;
    hit('pancada', bar, 0, 1);
    chord('acorde', bar, 0, prog[cycle(bar - intro, 8)]!.full.map(tr), s * 2.5, 1);
  }

  return {
    id: trackId(XOTE, seed, p),
    template: XOTE.id,
    seed,
    params: p,
    title: ch.orig ? XOTE.ref.title : titleOf(seed),
    genre: XOTE.genre,
    bpm: p.bpm,
    swing: 56,
    lufs: -11.5,
    key,
    form,
    mix: { reverbDecay: 1, delayTime: (60 / p.bpm) * 0.5, delayFeedback: 0.1, drive: 1.35, softness: 1.25, masterLowpass: 11500, pumpDepth: 0, pumpRelease: 0.2 },
    events,
    autos,
    accents: { pancada: 'drop', zabumba: 'kick', ta: 'snare', acorde: 'stab', lead: 'melody' },
  };
}

export const XOTE: Template = {
  id: 'xote',
  genre: 'xote / baião pé-de-serra',
  bpm: [104, 128],
  ref: { seed: 'xilo', title: 'Xilo', params: { bpm: 116, bars: 28, energy: 0.6 } },
  score,
};
